import { Link } from 'react-router-dom';
import BoardThumb from './BoardThumb';
import SideBadge from './SideBadge';
import DifficultyDots from './DifficultyDots';
import PopularityMeter from './PopularityMeter';
import type {
  Difficulty,
  Tier,
  OpeningStats,
  Popularity,
  Side,
} from '../../../shared/types';

/**
 * One opening, shown the same way everywhere it appears.
 *
 * Laid out **horizontally**: the board sits on the left and everything else
 * fills the space beside it. The earlier stacked version put the diagram on top
 * and the text underneath, which left the whole top-right of every tile empty —
 * a lot of dead space repeated forty times down a page.
 *
 * Three slots keep one component serving every context without any page
 * drawing its own version:
 *
 * - `corner` — an overflow menu or similar, in the header row rather than
 *   floating over the card, so it cannot obscure the side badge beneath it.
 * - `badge` — a status word like "Next up", inline with the name. Status is
 *   shown *on* the tile rather than by wrapping it in another box, which only
 *   looked deliberate on the one highlighted item and awkward on every other.
 * - `footer` — actions, below a divider.
 *
 * The whole tile is clickable via a stretched pseudo-element rather than by
 * wrapping everything in an anchor: buttons inside an anchor would both
 * navigate and act, and nesting interactive elements is invalid markup.
 */
export interface OpeningCardData {
  id: string;
  name: string;
  side: Side;
  fen: string;
  moves: string;
  summary: string;
  eco: string;
  tier: Tier;
  difficulty: Difficulty;
  popularity: Popularity;
  stats?: OpeningStats;
  /** Every line the opening has. */
  lineCount: number;
}

export const TIER_LABEL: Record<Tier, string> = {
  beginner: 'Start here',
  intermediate: 'Next steps',
  advanced: 'Advanced',
  master: 'Master',
};

export default function OpeningCard({
  opening,
  corner,
  badge,
  footer,
  eyebrow,
  /** Adds a highlight to the tile itself — used for the next step on the path. */
  highlight = false,
  /** Dims the tile without hiding it, for things already finished. */
  muted = false,
  className = '',
}: {
  opening: OpeningCardData;
  corner?: React.ReactNode;
  badge?: React.ReactNode;
  footer?: React.ReactNode;
  eyebrow?: React.ReactNode;
  highlight?: boolean;
  muted?: boolean;
  className?: string;
}) {
  const classes = [
    'ocard',
    highlight ? 'ocard--highlight' : '',
    muted ? 'ocard--muted' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={classes}>
      <div className="ocard-main">
        <Link to={`/openings/${opening.id}`} className="ocard-thumb" tabIndex={-1} aria-hidden="true">
          <BoardThumb fen={opening.fen} orientation={opening.side} size={124} />
        </Link>

        <div className="ocard-body">
          <div className="ocard-head">
            <div className="ocard-titles">
              {eyebrow && <span className="ocard-eyebrow">{eyebrow}</span>}
              <h3 className="ocard-name">
                <Link to={`/openings/${opening.id}`} className="ocard-link">
                  {opening.name}
                </Link>
              </h3>
            </div>
            {corner && <div className="ocard-corner">{corner}</div>}
          </div>

          <div className="ocard-badges">
            <SideBadge side={opening.side} />
            {badge}
          </div>

          <code className="opening-moves">{opening.moves}</code>
          <p className="ocard-summary">{opening.summary}</p>

          <div className="ocard-foot">
            <span className="tag">{opening.eco}</span>
            <span className="muted">{opening.lineCount} lines</span>
            <span className={`tier-chip tier-chip--${opening.tier}`}>
              {TIER_LABEL[opening.tier]}
            </span>
            <DifficultyDots level={opening.difficulty} />
            <PopularityMeter value={opening.popularity} stats={opening.stats} compact />
          </div>
        </div>
      </div>

      {footer && <div className="ocard-footer">{footer}</div>}
    </article>
  );
}
